import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Package, 
  ArrowLeft, 
  CheckCircle,
  Loader2,
  Truck,
  MapPin,
  Calculator,
  AlertTriangle,
  ChevronsUpDown,
  Check,
  Bookmark,
  ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReceiveDeliveryPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get('supplier');
  const itemsParam = searchParams.get('items');
  const isMultiReceive = !!supplierId && !!itemsParam;
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  
  // Delivery details
  const [receiveItems, setReceiveItems] = useState([]);
  const [freightCost, setFreightCost] = useState("");
  const [freightDistribution, setFreightDistribution] = useState("per_item");
  const [notes, setNotes] = useState("");
  const [defaultLocation, setDefaultLocation] = useState("MOTTAGEN ORDER");
  const [openLocationPopover, setOpenLocationPopover] = useState({});
  
  // Reservations dialog
  const [showReservationsDialog, setShowReservationsDialog] = useState(false);
  const [convertedReservations, setConvertedReservations] = useState([]);

  useEffect(() => {
    if (isMultiReceive) {
      fetchMultipleOrders();
    } else if (orderId) {
      fetchOrder();
    } else {
      toast.error("Ingen order angiven");
      navigate("/purchasing/orders");
    }
    fetchLocations();
  }, [orderId, isMultiReceive]);
  
  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      // Convert strings to objects with name property, then sort
      const locationObjects = response.data.map(loc => 
        typeof loc === 'string' ? { name: loc, id: loc } : loc
      );
      // Sort locations: indoor first, then outdoor
      const sorted = locationObjects.sort((a, b) => {
        const aOutdoor = a.name.toLowerCase().includes('utomhus') || a.name.toLowerCase().includes('ute') || a.name.startsWith('U');
        const bOutdoor = b.name.toLowerCase().includes('utomhus') || b.name.toLowerCase().includes('ute') || b.name.startsWith('U');
        if (aOutdoor && !bOutdoor) return 1;
        if (!aOutdoor && bOutdoor) return -1;
        return a.name.localeCompare(b.name, 'sv');
      });
      setLocations(sorted);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchMultipleOrders = async () => {
    try {
      // Parse items from URL parameter (format: "orderId:ean,orderId:ean,...")
      const itemKeys = itemsParam ? itemsParam.split(',').map(key => {
        const [order_id, ean] = key.split(':');
        return { order_id, ean };
      }) : [];
      
      if (itemKeys.length === 0) {
        toast.error("Inga produkter att ta emot");
        navigate("/purchasing/orders");
        return;
      }
      
      // Get unique order IDs
      const orderIds = [...new Set(itemKeys.map(k => k.order_id))];
      
      // Fetch all orders
      const orderPromises = orderIds.map(id => axios.get(`${API}/purchasing/orders/${id}`));
      const orderResponses = await Promise.all(orderPromises);
      
      // Fetch supplier name
      let supplierName = "Okänd leverantör";
      try {
        const supplierRes = await axios.get(`${API}/purchasing/suppliers/${supplierId}`);
        supplierName = supplierRes.data.company_name;
      } catch (e) {
        console.error("Could not fetch supplier:", e);
      }
      
      // Use first order as the main order for display
      const firstOrder = orderResponses[0]?.data;
      if (firstOrder) {
        setOrder({
          ...firstOrder,
          supplier_name: supplierName
        });
      }
      
      // Build receive items from selected products
      const items = [];
      for (const key of itemKeys) {
        // Find the order this product belongs to
        const orderData = orderResponses.find(r => r.data.id === key.order_id)?.data;
        const orderItem = orderData?.items.find(i => i.ean === key.ean);
        
        if (orderItem) {
          const remaining = orderItem.quantity - (orderItem.received_quantity || 0);
          if (remaining > 0) {
            items.push({
              row_id: `${key.order_id}-${orderItem.ean}`,
              ean: orderItem.ean,
              artikelnummer: orderItem.artikelnummer,
              produktnamn: orderItem.produktnamn,
              ordered: orderItem.quantity,
              already_received: orderItem.received_quantity || 0,
              remaining: remaining,
              receive_quantity: remaining,
              location: "MOTTAGEN ORDER",
              unit_price: orderItem.unit_price,
              order_id: key.order_id,
              order_number: orderData.order_number || ""
            });
          }
        }
      }
      
      if (items.length === 0) {
        toast.error("Inga produkter att ta emot");
        navigate("/purchasing/orders");
        return;
      }
      
      setReceiveItems(items);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Kunde inte hämta ordrar");
      navigate("/purchasing/orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/purchasing/orders/${orderId}`);
      setOrder(response.data);
      
      // Initialize receive items with remaining quantities
      setReceiveItems(response.data.items.map((item, idx) => ({
        row_id: `${response.data.id}-${item.ean}-${idx}`,
        ean: item.ean,
        artikelnummer: item.artikelnummer,
        produktnamn: item.produktnamn,
        ordered: item.quantity,
        already_received: item.received_quantity || 0,
        remaining: item.quantity - (item.received_quantity || 0),
        receive_quantity: item.quantity - (item.received_quantity || 0),
        location: "MOTTAGEN ORDER",
        unit_price: item.unit_price,
        order_id: response.data.id,
        order_number: response.data.order_number || ""
      })));
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Kunde inte hämta order");
      navigate("/purchasing/orders");
    } finally {
      setLoading(false);
    }
  };

  const updateReceiveQuantity = (rowId, value) => {
    const qty = parseInt(value) || 0;
    setReceiveItems(prev => prev.map(item => {
      if (item.row_id === rowId) {
        return { ...item, receive_quantity: Math.min(Math.max(0, qty), item.remaining) };
      }
      return item;
    }));
  };

  const updateLocation = (rowId, location) => {
    setReceiveItems(prev => prev.map(item => {
      if (item.row_id === rowId) {
        return { ...item, location };
      }
      return item;
    }));
  };

  const applyDefaultLocation = () => {
    setReceiveItems(prev => prev.map(item => ({
      ...item,
      location: defaultLocation
    })));
    toast.success(`Lagerplats "${defaultLocation}" applicerad på alla produkter`);
  };

  const receiveAll = () => {
    setReceiveItems(prev => prev.map(item => ({
      ...item,
      receive_quantity: item.remaining
    })));
  };

  const handleSubmit = async () => {
    const itemsToReceive = receiveItems.filter(i => i.receive_quantity > 0);
    
    if (itemsToReceive.length === 0) {
      toast.error("Ange antal för minst en produkt");
      return;
    }
    
    // Check all items have locations
    const missingLocation = itemsToReceive.find(i => !i.location.trim());
    if (missingLocation) {
      toast.error(`Ange lagerplats för ${missingLocation.artikelnummer}`);
      return;
    }
    
    setSaving(true);
    try {
      let allConvertedReservations = [];
      
      // Always use batch endpoint to create a single delivery receipt
      // Get supplier info from the order
      let supplierId = order?.supplier_id || null;
      
      // Build batch payload with all items
      const batchPayload = {
        items: itemsToReceive.map(item => ({
          ean: item.ean || "",
          artikelnummer: item.artikelnummer || "",
          produktnamn: item.produktnamn || "",
          quantity: item.receive_quantity,
          location: item.location,
          unit_price: item.unit_price || 0,
          freight_per_unit: 0, // Will be calculated by backend
          supplier_id: supplierId,
          order_id: item.order_id || orderId
        })),
        supplier_id: supplierId,
        freight_cost: parseFloat(freightCost) || 0,
        freight_distribution: freightDistribution,
        notes: notes || `Leveransmottagning ${new Date().toISOString()}`
      };
      
      const response = await axios.post(`${API}/purchasing/quick-receive-batch`, batchPayload);
      
      // Collect converted reservations
      if (response.data.reservations_converted > 0) {
        // For now, just show count - could enhance to show details
      }
      
      toast.success(`${itemsToReceive.length} produkter mottagna`);
      
      // Navigate to received orders page
      navigate("/purchasing/orders/received");
      
    } catch (error) {
      console.error("Error receiving delivery:", error);
      toast.error(error.response?.data?.detail || "Kunde inte registrera leverans");
    } finally {
      setSaving(false);
    }
  };

  const totalToReceive = receiveItems.reduce((sum, i) => sum + i.receive_quantity, 0);
  const totalValue = receiveItems.reduce((sum, i) => sum + (i.unit_price * i.receive_quantity), 0);
  const freightPerItem = freightCost && totalToReceive > 0 
    ? (parseFloat(freightCost) / totalToReceive).toFixed(2)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/purchasing/orders">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-green-500" />
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Ta emot leverans
                </h1>
              </div>
              <p className="text-sm text-muted-foreground">
                Order {order.order_number} från {order.supplier_name}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        {/* Default Location */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label>Standardlagerplats för alla produkter</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between font-normal"
                    >
                      {defaultLocation || "Välj lagerplats..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök lagerplats..." />
                      <CommandList>
                        <CommandEmpty>Ingen lagerplats hittades</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="MOTTAGEN ORDER"
                            onSelect={() => setDefaultLocation("MOTTAGEN ORDER")}
                          >
                            <Check className={cn("mr-2 h-4 w-4", defaultLocation === "MOTTAGEN ORDER" ? "opacity-100" : "opacity-0")} />
                            MOTTAGEN ORDER
                          </CommandItem>
                          {locations.map((loc) => (
                            <CommandItem
                              key={loc.id || loc.name}
                              value={loc.name}
                              onSelect={() => setDefaultLocation(loc.name)}
                            >
                              <Check className={cn("mr-2 h-4 w-4", defaultLocation === loc.name ? "opacity-100" : "opacity-0")} />
                              {loc.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <Button variant="outline" onClick={applyDefaultLocation}>
                <MapPin className="h-4 w-4 mr-2" />
                Applicera på alla
              </Button>
              <Button variant="outline" onClick={receiveAll}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Ta emot alla
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Products to Receive */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase">Produkter att ta emot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {receiveItems.map((item) => (
              <Card key={item.row_id} className={`border-border ${item.remaining === 0 ? 'opacity-50' : ''}`}>
                <CardContent className="p-3">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-mono font-bold text-primary">{item.artikelnummer}</p>
                        <p className="text-xs text-muted-foreground font-mono">{item.ean}</p>
                        {item.order_number && isMultiReceive && (
                          <p className="text-xs text-muted-foreground mt-0.5">Order: {item.order_number}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">Beställt: {item.ordered}</Badge>
                          {item.already_received > 0 && (
                            <Badge variant="secondary">Redan mottaget: {item.already_received}</Badge>
                          )}
                          <Badge variant={item.remaining > 0 ? "default" : "secondary"}>
                            Kvar: {item.remaining}
                          </Badge>
                        </div>
                      </div>
                      {item.unit_price > 0 && (
                        <p className="text-sm text-muted-foreground">
                          {item.unit_price.toFixed(2)} SEK/st
                        </p>
                      )}
                    </div>
                    
                    {item.remaining > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Antal att ta emot</Label>
                          <Input
                            type="number"
                            min="0"
                            max={item.remaining}
                            value={item.receive_quantity}
                            onChange={(e) => updateReceiveQuantity(item.row_id, e.target.value)}
                            data-testid={`qty-input-${item.row_id}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Lagerplats</Label>
                          <Popover
                            open={openLocationPopover[item.row_id]}
                            onOpenChange={(open) => setOpenLocationPopover(prev => ({ ...prev, [item.row_id]: open }))}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between font-normal text-sm h-9"
                              >
                                {item.location || "Välj plats..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[200px] p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Sök..." className="h-8" />
                                <CommandList>
                                  <CommandEmpty>Ingen plats hittades</CommandEmpty>
                                  <CommandGroup>
                                    <CommandItem
                                      value="MOTTAGEN ORDER"
                                      onSelect={() => {
                                        updateLocation(item.row_id, "MOTTAGEN ORDER");
                                        setOpenLocationPopover(prev => ({ ...prev, [item.row_id]: false }));
                                      }}
                                    >
                                      <Check className={cn("mr-2 h-4 w-4", item.location === "MOTTAGEN ORDER" ? "opacity-100" : "opacity-0")} />
                                      MOTTAGEN ORDER
                                    </CommandItem>
                                    {locations.map((loc) => (
                                      <CommandItem
                                        key={loc.id || loc.name}
                                        value={loc.name}
                                        onSelect={() => {
                                          updateLocation(item.row_id, loc.name);
                                          setOpenLocationPopover(prev => ({ ...prev, [item.row_id]: false }));
                                        }}
                                      >
                                        <Check className={cn("mr-2 h-4 w-4", item.location === loc.name ? "opacity-100" : "opacity-0")} />
                                        {loc.name}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>

        {/* Freight Cost */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Fraktkostnad
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total fraktkostnad (SEK)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={freightCost}
                  onChange={(e) => setFreightCost(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Fördelning</Label>
                <Select value={freightDistribution} onValueChange={setFreightDistribution}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_item">Per styck (lika fördelning)</SelectItem>
                    <SelectItem value="proportional">Proportionellt mot värde</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {freightCost && totalToReceive > 0 && (
              <div className="p-3 bg-muted rounded-md text-sm">
                <p>
                  {freightDistribution === "per_item" 
                    ? `Frakt per enhet: ${freightPerItem} SEK`
                    : "Frakten fördelas proportionellt mot produkternas värde"
                  }
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Anteckningar</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="T.ex. fakturanummer, leveransnoteringar..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Summary & Submit */}
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sammanfattning</p>
                <p className="text-lg font-bold">
                  {totalToReceive} produkter att ta emot
                </p>
                {totalValue > 0 && (
                  <p className="text-sm">
                    Värde: {totalValue.toFixed(2)} SEK
                    {freightCost && ` + ${freightCost} SEK frakt`}
                  </p>
                )}
              </div>
              <Button 
                onClick={handleSubmit} 
                disabled={saving || totalToReceive === 0}
                className="gap-2 bg-green-600 hover:bg-green-700"
                size="lg"
                data-testid="receive-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Registrerar...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Ta emot leverans
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
      
      {/* Converted Reservations Dialog */}
      <Dialog open={showReservationsDialog} onOpenChange={setShowReservationsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-green-500" />
              Reservationer omvandlade till bokningar
            </DialogTitle>
            <DialogDescription>
              Följande produkter hade reservationer som nu har blivit bokningar. 
              Dessa kunder väntar på sina produkter.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3">
              {convertedReservations.map((res, idx) => (
                <Card key={idx} className="border-green-500/30 bg-green-500/5">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{res.artikelnummer || res.ean}</p>
                        {res.produktnamn && (
                          <p className="text-sm text-muted-foreground">{res.produktnamn}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-green-600 border-green-500">
                        {res.quantity} st
                      </Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-sm">
                      {res.customer_order_number && (
                        <Badge variant="secondary" className="gap-1">
                          <ClipboardList className="h-3 w-3" />
                          Order: {res.customer_order_number}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="gap-1">
                        <MapPin className="h-3 w-3" />
                        {res.location}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button onClick={() => {
              setShowReservationsDialog(false);
              navigate("/purchasing/orders/received");
            }}>
              Fortsätt till mottagna ordrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
